import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import departmentService from '../../services/departmentService';

const DepartmentForm = ({ department, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [parentDepartments, setParentDepartments] = useState([]);

  const validationSchema = yup.object().shape({
    name: yup.string()
      .required('Department name is required')
      .min(2, 'Department name must be at least 2 characters')
      .max(100, 'Department name must be less than 100 characters'),
    code: yup.string()
      .required('Department code is required')
      .min(2, 'Department code must be at least 2 characters')
      .max(20, 'Department code must be less than 20 characters')
      .matches(/^[A-Z0-9_]+$/, 'Department code must contain only uppercase letters, numbers, and underscores'),
    description: yup.string()
      .max(500, 'Description must be less than 500 characters'),
    managerId: yup.number()
      .nullable()
      .transform((value) => (isNaN(value) ? null : value)),
      parentId: yup.number()
    .nullable()
    .transform((value) => (isNaN(value) ? null : value)),
    location: yup.string()
      .max(100, 'Location must be less than 100 characters'),
    costCenter: yup.string()
      .max(50, 'Cost center must be less than 50 characters'),
    budget: yup.number()
      .nullable()
      .transform((value) => (isNaN(value) ? null : value))
      .min(0, 'Budget must be positive'),
    establishedDate: yup.date()
      .nullable()
      .max(new Date(), 'Established date cannot be in the future'),
    isActive: yup.boolean()
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      managerId: null,
      parentId: null,
      location: '',
      costCenter: '',
      budget: null,
      establishedDate: null,
      isActive: true
    }
  });

  useEffect(() => {
    loadFormData();
  }, [department]);

  const loadFormData = async () => {
    try {
      // Load available managers
      const managersResponse = await departmentService.getAvailableManagers();
      if (managersResponse.success) {
        setAvailableManagers(managersResponse.data.managers || []);
      }

      // Load parent departments
      const departmentsResponse = await departmentService.getDepartments({ limit: 1000 });
      if (departmentsResponse.success) {
        setParentDepartments(departmentsResponse.data.departments || []);
      }

      // Set form values if editing
      if (department) {
        reset({
          name: department.name || '',
          code: department.code || '',
          description: department.description || '',
          managerId: department.managerId || null,
          parentId: department.parentId || null,
          location: department.location || '',
          costCenter: department.costCenter || '',
          budget: department.budget || null,
          establishedDate: department.establishedDate ? new Date(department.establishedDate).toISOString().split('T')[0] : null,
          isActive: department.isActive !== undefined ? department.isActive : true
        });
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      toast.error('Failed to load form data');
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Transform data
      const departmentData = {
        ...data,
        managerId: data.managerId || null,
        parentId: data.parentId || null,
        budget: data.budget || null,
        establishedDate: data.establishedDate || null
      };

      if (department) {
        // Update existing department
        await departmentService.updateDepartment(department.id, departmentData);
        toast.success('Department updated successfully');
      } else {
        // Create new department
        await departmentService.createDepartment(departmentData);
        toast.success('Department created successfully');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving department:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to save department');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">
                {department ? 'Edit Department' : 'Create New Department'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Department Name */}
              <div className="md:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Department Name *
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name')}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter department name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* Department Code */}
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  Department Code *
                </label>
                <input
                  type="text"
                  id="code"
                  {...register('code')}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.code ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="e.g., IT_DEPT"
                />
                {errors.code && (
                  <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
                )}
              </div>

              {/* Parent Department */}
              <div>
                <label htmlFor="parentId" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Department
                </label>
                <select
                  id="parentId"
                  {...register('parentId')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No Parent Department</option>
                  {parentDepartments
                    .filter(dept => !department || dept.id !== department.id)
                    .map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))
                  }
                </select>
              </div>

              {/* Manager */}
              <div>
                <label htmlFor="managerId" className="block text-sm font-medium text-gray-700 mb-1">
                  Department Manager
                </label>
                <select
                  id="managerId"
                  {...register('managerId')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No Manager Assigned</option>
                  {availableManagers.map(manager => (
                    <option key={manager.id} value={manager.id}>
                      {manager.firstName} {manager.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <div className="relative">
                  <MapPinIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    id="location"
                    {...register('location')}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter location"
                  />
                </div>
              </div>

              {/* Cost Center */}
              <div>
                <label htmlFor="costCenter" className="block text-sm font-medium text-gray-700 mb-1">
                  Cost Center
                </label>
                <input
                  type="text"
                  id="costCenter"
                  {...register('costCenter')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter cost center"
                />
              </div>

              {/* Budget */}
              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                  Budget
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    id="budget"
                    {...register('budget')}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                {errors.budget && (
                  <p className="mt-1 text-sm text-red-600">{errors.budget.message}</p>
                )}
              </div>

              {/* Established Date */}
              <div>
                <label htmlFor="establishedDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Established Date
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    id="establishedDate"
                    {...register('establishedDate')}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {errors.establishedDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.establishedDate.message}</p>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  {...register('description')}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter department description"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              {/* Active Status */}
              <div className="md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    {...register('isActive')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Department is active
                  </label>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  department ? 'Update Department' : 'Create Department'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DepartmentForm; 